/**
 * Helper utility for handling employee discounts
 */

interface DiscountInfo {
  isEligible: boolean;
  discountPercentage: number;
  promotionCode: string;
  discountMessage: string;
}

/**
 * Check if user is eligible for employee first-purchase discount
 * A user is eligible if they have store.privileged permission (Horwath employee)
 * but NOT market_compass.view permission (never made a purchase)
 * Discount only applies to monthly subscriptions
 */
export function checkEmployeeFirstPurchaseDiscount(
  permissions: string[],
  interval?: 'month' | 'year' | 'monthly' | 'yearly'
): DiscountInfo {
  const hasStorePrivileged = permissions.includes('store.privileged');
  const hasMarketCompassView = permissions.includes('market_compass.view');
  
  // Check if this is a monthly interval
  const isMonthly = interval === 'month' || interval === 'monthly';
  
  // Eligible if they have store.privileged but NOT market_compass.view
  // AND it's a monthly subscription
  const isEligible = hasStorePrivileged && !hasMarketCompassView && isMonthly;
  
  return {
    isEligible,
    discountPercentage: isEligible ? 99 : 0,
    promotionCode: 'HHTLFIRST',
    discountMessage: isEligible 
      ? 'Horwath HTL Licensee: 99% off for the 1st month, you may cancel at any time' 
      : ''
  };
}

/**
 * Apply discount to a price
 */
export function applyDiscount(
  originalPrice: number, 
  discountPercentage: number
): number {
  if (discountPercentage <= 0 || discountPercentage > 100) {
    return originalPrice;
  }
  
  const discountAmount = Math.floor(originalPrice * (discountPercentage / 100));
  const discountedPrice = originalPrice - discountAmount;
  
  // Ensure minimum price of 1 cent
  return Math.max(discountedPrice, 1);
}

/**
 * Format a price with discount display
 */
export function formatPriceWithDiscount(
  originalPrice: number,
  discountedPrice: number,
  currency: string = 'USD'
): {
  originalFormatted: string;
  discountedFormatted: string;
  savingsFormatted: string;
  hasDiscount: boolean;
} {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  });
  
  const originalFormatted = formatter.format(originalPrice / 100);
  const discountedFormatted = formatter.format(discountedPrice / 100);
  const savingsFormatted = formatter.format((originalPrice - discountedPrice) / 100);
  
  return {
    originalFormatted,
    discountedFormatted,
    savingsFormatted,
    hasDiscount: discountedPrice < originalPrice
  };
}